import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import { consentActions, consentSelectors } from 'store/consent'
import { useModalActions } from 'providers/ModalProvider'
import { UIButton } from 'components/ui/Button'
import { useGetSetting } from 'api/settings'

interface CookieConsentProps {
  /** 'widget' = sits inside the rounded widget box. 'page' = fixed bottom bar on the fullscreen /chat layout. */
  surface?: 'widget' | 'page'
}

export const CookieConsent = ({ surface = 'widget' }: CookieConsentProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const decided = useSelector(consentSelectors.decided)
  const { showCookiePreferences } = useModalActions()
  const { setting } = useGetSetting()
  const bannerEnabled = setting?.cookieBannerEnabled === true

  // Banner only in opt-in mode; opt-out auto-accepts via useCookieConsentBootstrap.
  if (decided || !bannerEnabled) return null

  const decide = (analytics: boolean) => dispatch(consentActions.setAnalyticsConsent(analytics))

  return (
    <div
      className={clsx(
        'tw-z-40 tw-p-3',
        surface === 'page' ? 'tw-fixed tw-bottom-0 tw-right-0' : 'tw-absolute tw-inset-x-0 tw-bottom-0',
      )}
    >
      <div
        className={clsx(
          'animate-slide-up-modal tw-rounded-2xl tw-p-4',
          surface === 'page' ? 'tw-w-[440px] tw-max-w-[calc(100vw-1.5rem)]' : 'tw-w-full tw-max-w-2xl',
        )}
        style={{
          background: 'var(--surface-solid)',
          border: '1px solid var(--border-default)',
          boxShadow: '0 12px 32px -12px rgba(0, 0, 0, 0.35)',
        }}
      >
        <p className="tw-m-0 tw-text-sm tw-font-semibold" style={{ color: 'var(--text-primary)' }}>
          {t('cookieBanner.title')}
        </p>
        <p className="tw-mb-3 tw-mt-1 tw-text-xs tw-leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {t('cookieBanner.message')}{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="tw-underline"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('cookieBanner.privacy')}
          </a>{' '}
          ·{' '}
          <a
            href="/cookie-policy"
            target="_blank"
            rel="noreferrer"
            className="tw-underline"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('cookieBanner.cookie')}
          </a>
        </p>

        <div className="tw-flex tw-items-center tw-gap-2">
          {/* Accept and Reject share equal prominence (GDPR): same size, side by side. */}
          <UIButton variant="secondary" className="tw-flex-1" onClick={() => decide(false)}>
            {t('cookieBanner.reject')}
          </UIButton>
          <UIButton variant="primary" className="tw-flex-1" onClick={() => decide(true)}>
            {t('cookieBanner.accept')}
          </UIButton>
        </div>

        <button
          type="button"
          onClick={() => showCookiePreferences()}
          className="tw-mt-2 tw-w-full tw-bg-transparent tw-text-center tw-text-xs tw-underline"
          style={{ color: 'var(--text-tertiary)' }}
        >
          {t('cookieBanner.customize')}
        </button>
      </div>
    </div>
  )
}
